import jsPDF from 'jspdf';
import { toPng } from 'html-to-image';
import { getNodesBounds, getViewportForBounds } from '@xyflow/react';
import { useStore, type Sheet, type AnyNode, type WireEdge } from './store';
import type { ProjectMeta } from './types';

/**
 * Multi-page PDF export with an ASME/ISO-style title block on every page.
 *
 * Layout:
 *   • A3 landscape (420 × 297 mm) — fits real schematic detail.
 *   • Page 1 = cover sheet: project name / author / description + sheet TOC.
 *   • Pages 2+ = one per sheet, in project-tree order.
 *     - block / schematic → captured as PNG via React Flow's viewport
 *       (transparently switches the active sheet, captures, restores).
 *     - requirements → rendered as a table.
 *     - interconnect → rendered as bundle list with wire counts.
 *     - document → rendered as section headings + bodies.
 *   • Bottom-right title block on every page (project name, sheet title,
 *     author, date, sheet number, drawing number).
 */

// A3 landscape dimensions (mm)
const PAGE_W = 420;
const PAGE_H = 297;
const MARGIN = 10;
const TB_W = 180; // title block width
const TB_H = 35; // title block height

const LOGO_KEY = 'skema:pdf-logo';

/**
 * jsPDF's built-in fonts (helvetica/times/courier) only cover Latin-1, so
 * Turkish ş/ğ/ı/İ/ç/ü/ö are rendered with broken metrics — letters end up
 * with weird gaps. Embedding a TTF font would be cleaner but adds 100+ KB
 * to the bundle; for engineering drawings the assembler reads the text the
 * same way either way, so we transliterate to ASCII and ship it.
 */
function asciiSafe(s: string | undefined | null): string {
  if (!s) return '';
  const map: Record<string, string> = {
    ş: 's',
    Ş: 'S',
    ğ: 'g',
    Ğ: 'G',
    ı: 'i',
    İ: 'I',
    ç: 'c',
    Ç: 'C',
    ü: 'u',
    Ü: 'U',
    ö: 'o',
    Ö: 'O',
    â: 'a',
    Â: 'A',
    î: 'i',
    Î: 'I',
    û: 'u',
    Û: 'U',
    '—': '-',
    '–': '-',
    '“': '"',
    '”': '"',
    '‘': "'",
    '’': "'",
    '…': '...',
    '×': 'x',
    '·': '-',
    '→': '->',
    '⏚': 'PE',
  };
  return s.replace(/[^\x00-\x7F]/g, (c) => map[c] ?? '?');
}

/** Optional logo data URL the user uploaded for their drawing-set title
 *  block. Lives in localStorage; null when not set (empty area in TB). */
export function getPdfLogoDataUrl(): string | null {
  try {
    return localStorage.getItem(LOGO_KEY);
  } catch {
    return null;
  }
}

export function setPdfLogoDataUrl(dataUrl: string | null): void {
  try {
    if (dataUrl) localStorage.setItem(LOGO_KEY, dataUrl);
    else localStorage.removeItem(LOGO_KEY);
  } catch {
    // ignore
  }
}

/** Render the standard title block in the bottom-right corner. */
function drawTitleBlock(
  doc: jsPDF,
  meta: ProjectMeta,
  info: {
    sheetNo: number;
    totalSheets: number;
    sheetTitle: string;
    drawingNo: string;
  },
  logoDims: { width: number; height: number; dataUrl: string } | null,
) {
  const x = PAGE_W - MARGIN - TB_W;
  const y = PAGE_H - MARGIN - TB_H;

  doc.setLineWidth(0.5);
  doc.setDrawColor(20);
  // Outer frame
  doc.rect(x, y, TB_W, TB_H);

  // Logo strip occupies the full left side of the title block so the
  // image sits visually centred against the whole block (not just the
  // top row). The remaining 135mm hosts the project / sheet text on the
  // top row and four equal columns on the bottom row.
  const logoStripW = logoDims ? 45 : 0;
  const titleRowH = 14;
  const remainingW = TB_W - logoStripW;
  const colW = remainingW / 4;

  // Title-row separator (skips the logo strip)
  doc.line(x + logoStripW, y + titleRowH, x + TB_W, y + titleRowH);
  // Bottom-row column separators
  for (let i = 1; i < 4; i += 1) {
    doc.line(
      x + logoStripW + colW * i,
      y + titleRowH,
      x + logoStripW + colW * i,
      y + TB_H,
    );
  }

  if (logoDims) {
    // Aspect-fit centred inside the entire 45 × 35 mm strip.
    const pad = 3;
    const maxW = logoStripW - pad * 2;
    const maxH = TB_H - pad * 2;
    const aspect = logoDims.width / logoDims.height;
    let drawW = maxW;
    let drawH = drawW / aspect;
    if (drawH > maxH) {
      drawH = maxH;
      drawW = drawH * aspect;
    }
    doc.addImage(
      logoDims.dataUrl,
      'PNG',
      x + (logoStripW - drawW) / 2,
      y + (TB_H - drawH) / 2,
      drawW,
      drawH,
    );
    // Vertical separator between logo strip and the rest
    doc.setDrawColor(180);
    doc.line(x + logoStripW, y, x + logoStripW, y + TB_H);
    doc.setDrawColor(20);
  }

  const textX = x + logoStripW;

  // Top row — project + sheet title
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(7);
  doc.setTextColor(110);
  doc.text('PROJECT', textX + 2, y + 4);
  doc.setTextColor(20);
  doc.setFontSize(11);
  doc.text(asciiSafe(truncate(meta.name || '(untitled project)', 50)), textX + 2, y + 9);

  doc.setFontSize(7);
  doc.setTextColor(110);
  doc.text('SHEET', textX + 2, y + 12);
  doc.setTextColor(20);
  doc.setFontSize(10);
  doc.text(asciiSafe(truncate(info.sheetTitle, 50)), textX + 22, y + 12);

  // Bottom row labels
  doc.setFontSize(6);
  doc.setTextColor(110);
  doc.text('AUTHOR', textX + 2, y + titleRowH + 4);
  doc.text('DATE', textX + colW + 2, y + titleRowH + 4);
  doc.text('SHEET', textX + colW * 2 + 2, y + titleRowH + 4);
  doc.text('DRAWING NO', textX + colW * 3 + 2, y + titleRowH + 4);

  // Bottom row values
  doc.setFontSize(10);
  doc.setTextColor(20);
  doc.setFont('helvetica', 'normal');
  doc.text(asciiSafe(truncate(meta.author || '-', 30)), textX + 2, y + titleRowH + 11);
  doc.text(formatDate(meta.updatedAt), textX + colW + 2, y + titleRowH + 11);
  doc.text(
    `${info.sheetNo} / ${info.totalSheets}`,
    textX + colW * 2 + 2,
    y + titleRowH + 11,
  );
  doc.text(
    asciiSafe(truncate(info.drawingNo, 18)),
    textX + colW * 3 + 2,
    y + titleRowH + 11,
  );

  // Generated-by stamp under the title block
  doc.setFontSize(6);
  doc.setTextColor(140);
  doc.text(
    `Skematiq v${typeof __APP_VERSION__ !== 'undefined' ? __APP_VERSION__ : ''}`,
    x + TB_W - 2,
    y + TB_H + 3,
    { align: 'right' },
  );
}

/** Load the logo image into memory once so we know its native size. */
async function resolveLogo(): Promise<{
  width: number;
  height: number;
  dataUrl: string;
} | null> {
  const url = getPdfLogoDataUrl();
  if (!url) return null;
  return new Promise((resolve) => {
    const img = new Image();
    img.onload = () =>
      resolve({ width: img.width, height: img.height, dataUrl: url });
    img.onerror = () => resolve(null);
    img.src = url;
  });
}

/** Standard outer drawing border + zone markers at top/bottom. */
function drawPageFrame(doc: jsPDF) {
  doc.setLineWidth(0.7);
  doc.setDrawColor(40);
  doc.rect(MARGIN, MARGIN, PAGE_W - MARGIN * 2, PAGE_H - MARGIN * 2);
}

function truncate(s: string, max: number): string {
  if (s.length <= max) return s;
  return s.slice(0, max - 1) + '…';
}

function formatDate(iso: string): string {
  if (!iso) return new Date().toISOString().slice(0, 10);
  return iso.slice(0, 10);
}

// ─────────────────────────────────────────────────────────────────────
// Cover page
// ─────────────────────────────────────────────────────────────────────

function drawCoverPage(doc: jsPDF, meta: ProjectMeta, sheets: Sheet[]) {
  drawPageFrame(doc);
  // Project name — big
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(38);
  doc.setTextColor(20);
  doc.text(asciiSafe(meta.name || '(untitled project)'), MARGIN + 10, MARGIN + 30);

  // Subtitle
  doc.setFontSize(14);
  doc.setTextColor(100);
  doc.setFont('helvetica', 'normal');
  doc.text('System Design Drawing Set', MARGIN + 10, MARGIN + 42);

  // Metadata table
  const metaY = MARGIN + 60;
  const labelW = 35;
  const lineH = 8;
  doc.setFontSize(10);
  const rows: [string, string][] = [
    ['Author', meta.author || '-'],
    ['Created', formatDate(meta.createdAt)],
    ['Updated', formatDate(meta.updatedAt)],
    ['Sheets', String(sheets.length)],
  ];
  rows.forEach(([k, v], i) => {
    doc.setTextColor(120);
    doc.text(k.toUpperCase(), MARGIN + 10, metaY + i * lineH);
    doc.setTextColor(20);
    doc.text(asciiSafe(v), MARGIN + 10 + labelW, metaY + i * lineH);
  });

  // Description (wrapped)
  if (meta.description?.trim()) {
    const descY = metaY + rows.length * lineH + 8;
    doc.setFontSize(10);
    doc.setTextColor(120);
    doc.text('DESCRIPTION', MARGIN + 10, descY);
    doc.setTextColor(20);
    const wrapped = doc.splitTextToSize(asciiSafe(meta.description), 200);
    doc.text(wrapped, MARGIN + 10, descY + 6);
  }

  // Sheet table of contents — right side
  const tocX = PAGE_W - MARGIN - 180;
  const tocY = MARGIN + 60;
  doc.setFontSize(10);
  doc.setTextColor(120);
  doc.text('SHEET INDEX', tocX, tocY);
  doc.setLineWidth(0.3);
  doc.setDrawColor(180);
  doc.line(tocX, tocY + 2, tocX + 170, tocY + 2);

  doc.setFontSize(9);
  doc.setTextColor(20);
  const rowLimit = PAGE_H - MARGIN - TB_H - 10;
  let shown = 0;
  for (let i = 0; i < sheets.length; i += 1) {
    const ry = tocY + 10 + i * 6;
    if (ry > rowLimit) break;
    const sh = sheets[i];
    doc.setFont('helvetica', 'normal');
    doc.text(String(i + 2).padStart(3, ' '), tocX, ry);
    doc.text(`(${sh.kind})`, tocX + 14, ry);
    doc.setFont('helvetica', 'bold');
    doc.text(asciiSafe(truncate(sh.name, 60)), tocX + 36, ry);
    shown = i + 1;
  }
  if (shown < sheets.length) {
    doc.setFont('helvetica', 'italic');
    doc.setTextColor(120);
    doc.text(
      `… and ${sheets.length - shown} more sheet(s) — each has its own numbered page`,
      tocX,
      tocY + 10 + shown * 6,
    );
  }
}

// ─────────────────────────────────────────────────────────────────────
// Schematic / block — capture canvas to PNG, embed
// ─────────────────────────────────────────────────────────────────────

async function captureSheetPng(sheet: Sheet): Promise<string | null> {
  const originalActive = useStore.getState().activeSheetId;
  if (originalActive !== sheet.id) {
    useStore.getState().setActiveSheet(sheet.id);
    // Two RAFs + a small delay let React Flow rebuild the viewport.
    await new Promise<void>((r) => requestAnimationFrame(() => r()));
    await new Promise<void>((r) => requestAnimationFrame(() => r()));
    await new Promise<void>((r) => setTimeout(r, 250));
  }

  const viewportEl = document.querySelector<HTMLElement>('.react-flow__viewport');
  let dataUrl: string | null = null;
  if (viewportEl) {
    try {
      const nodes = sheet.nodes as AnyNode[];
      const edges = sheet.edges as WireEdge[];
      if (nodes.length === 0) {
        dataUrl = null;
      } else {
        const padding = 80;
        const nodeBounds = getNodesBounds(nodes);
        let x1 = nodeBounds.x;
        let y1 = nodeBounds.y;
        let x2 = nodeBounds.x + nodeBounds.width;
        let y2 = nodeBounds.y + nodeBounds.height;
        const WIRE_MARGIN = 60;
        x1 -= WIRE_MARGIN;
        y1 -= WIRE_MARGIN;
        x2 += WIRE_MARGIN;
        y2 += WIRE_MARGIN;
        for (const e of edges) {
          const d = e.data as { midX?: number; midY?: number; midX2?: number; midY2?: number } | undefined;
          for (const mx of [d?.midX, d?.midX2]) {
            if (typeof mx === 'number') {
              x1 = Math.min(x1, mx - 20);
              x2 = Math.max(x2, mx + 20);
            }
          }
          for (const my of [d?.midY, d?.midY2]) {
            if (typeof my === 'number') {
              y1 = Math.min(y1, my - 20);
              y2 = Math.max(y2, my + 20);
            }
          }
        }
        const bounds = { x: x1, y: y1, width: x2 - x1, height: y2 - y1 };
        const imageWidth = Math.ceil(bounds.width) + padding * 2;
        const imageHeight = Math.ceil(bounds.height) + padding * 2;
        const viewport = getViewportForBounds(bounds, imageWidth, imageHeight, 0.1, 2, 0.05);
        // PDFs go on white paper — always capture against a clean light
        // background regardless of the user's theme preference.
        const bgColor = '#ffffff';
        dataUrl = await toPng(viewportEl, {
          backgroundColor: bgColor,
          pixelRatio: 2,
          width: imageWidth,
          height: imageHeight,
          style: {
            width: `${imageWidth}px`,
            height: `${imageHeight}px`,
            transform: `translate(${viewport.x}px, ${viewport.y}px) scale(${viewport.zoom})`,
          },
        });
      }
    } catch {
      dataUrl = null;
    }
  }

  if (originalActive !== sheet.id) {
    useStore.getState().setActiveSheet(originalActive);
    await new Promise<void>((r) => requestAnimationFrame(() => r()));
  }
  return dataUrl;
}

function drawSheetTitle(doc: jsPDF, sheet: Sheet) {
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(14);
  doc.setTextColor(20);
  doc.text(asciiSafe(sheet.name), MARGIN + 6, MARGIN + 8);
  doc.setFontSize(8);
  doc.setTextColor(110);
  doc.setFont('helvetica', 'normal');
  doc.text(sheet.kind.toUpperCase(), MARGIN + 6, MARGIN + 13);
}

async function drawGraphSheet(doc: jsPDF, sheet: Sheet) {
  drawPageFrame(doc);
  drawSheetTitle(doc, sheet);
  const png = await captureSheetPng(sheet);
  if (!png) {
    doc.setFontSize(10);
    doc.setTextColor(120);
    doc.text(
      sheet.nodes.length === 0
        ? 'This sheet is empty — no components placed yet.'
        : 'Could not capture canvas (see app console for details).',
      MARGIN + 10,
      MARGIN + 30,
    );
    return;
  }
  // Fit image into the available page area above the title block, below
  // the sheet header.
  const availX = MARGIN + 6;
  const availY = MARGIN + 18;
  const availW = PAGE_W - MARGIN * 2 - 12;
  const availH = PAGE_H - MARGIN * 2 - TB_H - 22;
  // jsPDF will scale the PNG to fit while preserving aspect.
  const img = new Image();
  await new Promise<void>((r, rej) => {
    img.onload = () => r();
    img.onerror = () => rej(new Error('image decode'));
    img.src = png;
  });
  const aspect = img.width / img.height;
  let drawW = availW;
  let drawH = drawW / aspect;
  if (drawH > availH) {
    drawH = availH;
    drawW = drawH * aspect;
  }
  const offsetX = availX + (availW - drawW) / 2;
  const offsetY = availY + (availH - drawH) / 2;
  doc.addImage(png, 'PNG', offsetX, offsetY, drawW, drawH);
}

// ─────────────────────────────────────────────────────────────────────
// Requirements / interconnect / document — text rendering
// ─────────────────────────────────────────────────────────────────────

function drawRequirementsSheet(doc: jsPDF, sheet: Sheet) {
  drawPageFrame(doc);
  drawSheetTitle(doc, sheet);
  const reqs = sheet.requirements ?? [];
  if (reqs.length === 0) {
    doc.setFontSize(10);
    doc.setTextColor(120);
    doc.text('No requirements yet.', MARGIN + 10, MARGIN + 30);
    return;
  }
  const startY = MARGIN + 24;
  let y = startY;
  doc.setFontSize(8);
  doc.setTextColor(120);
  doc.text('#', MARGIN + 6, y);
  doc.text('CATEGORY', MARGIN + 26, y);
  doc.text('PRI', MARGIN + 70, y);
  doc.text('STATUS', MARGIN + 86, y);
  doc.text('TITLE / DESCRIPTION', MARGIN + 116, y);
  y += 4;
  doc.setLineWidth(0.3);
  doc.setDrawColor(180);
  doc.line(MARGIN + 6, y, PAGE_W - MARGIN - 6, y);
  y += 4;
  doc.setTextColor(20);
  for (const r of reqs) {
    if (y > PAGE_H - MARGIN - TB_H - 12) break;
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(9);
    doc.text(asciiSafe(r.reqNumber), MARGIN + 6, y);
    doc.setFont('helvetica', 'normal');
    doc.text(asciiSafe(r.category), MARGIN + 26, y);
    doc.text(asciiSafe(r.priority), MARGIN + 70, y);
    doc.text(asciiSafe(r.status), MARGIN + 86, y);
    const titleW = PAGE_W - MARGIN - 6 - (MARGIN + 116);
    const wrapped = doc.splitTextToSize(
      asciiSafe(`${r.title}${r.description ? '  -  ' + r.description : ''}`),
      titleW,
    ) as string[];
    doc.text(wrapped, MARGIN + 116, y);
    y += Math.max(6, wrapped.length * 4) + 2;
  }
}

function drawInterconnectSheet(doc: jsPDF, sheet: Sheet) {
  drawPageFrame(doc);
  drawSheetTitle(doc, sheet);
  const bundles = sheet.bundles ?? [];
  if (bundles.length === 0) {
    doc.setFontSize(10);
    doc.setTextColor(120);
    doc.text('No cable bundles yet.', MARGIN + 10, MARGIN + 30);
    return;
  }

  // Wire counts per harness across the whole project (this sheet only stores
  // bundle metadata; the actual conductors live on schematics).
  const wireCounts = new Map<string, number>();
  for (const sh of useStore.getState().sheets) {
    if (sh.kind !== 'schematic') continue;
    for (const e of sh.edges) {
      const hid = e.data?.harnessId;
      if (!hid) continue;
      wireCounts.set(hid, (wireCounts.get(hid) ?? 0) + 1);
    }
  }

  let y = MARGIN + 24;
  for (const b of bundles) {
    if (y > PAGE_H - MARGIN - TB_H - 24) break;
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(11);
    doc.setTextColor(20);
    doc.text(asciiSafe(b.name || '(unnamed)'), MARGIN + 6, y);
    const cnt = wireCounts.get(b.id) ?? 0;
    doc.setFontSize(8);
    doc.setTextColor(100);
    doc.text(`${cnt} wire${cnt === 1 ? '' : 's'}`, PAGE_W - MARGIN - 30, y);
    y += 5;

    doc.setFont('helvetica', 'normal');
    doc.setFontSize(9);
    doc.setTextColor(60);
    const meta1 =
      (b.fromUnit && b.toUnit ? `${b.fromUnit} -> ${b.toUnit}` : '') +
      (b.length ? `   -   Length: ${b.length}` : '') +
      (b.conduit ? `   -   ${b.conduit}` : '');
    if (meta1) {
      doc.text(asciiSafe(meta1), MARGIN + 6, y);
      y += 5;
    }
    if (b.cables) {
      doc.setFontSize(8);
      doc.setTextColor(40);
      const wrapped = doc.splitTextToSize(
        asciiSafe(b.cables),
        PAGE_W - MARGIN * 2 - 12,
      ) as string[];
      doc.text(wrapped, MARGIN + 12, y);
      y += wrapped.length * 4 + 2;
    }
    y += 4;
    doc.setLineWidth(0.2);
    doc.setDrawColor(220);
    doc.line(MARGIN + 6, y, PAGE_W - MARGIN - 6, y);
    y += 6;
  }
}

function drawDocumentSheet(doc: jsPDF, sheet: Sheet) {
  drawPageFrame(doc);
  drawSheetTitle(doc, sheet);
  const sections = sheet.sections ?? [];
  if (sections.length === 0) {
    doc.setFontSize(10);
    doc.setTextColor(120);
    doc.text('Empty document.', MARGIN + 10, MARGIN + 30);
    return;
  }
  let y = MARGIN + 24;
  for (const s of sections) {
    if (y > PAGE_H - MARGIN - TB_H - 14) break;
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(13);
    doc.setTextColor(20);
    doc.text(asciiSafe(s.heading || '(untitled section)'), MARGIN + 6, y);
    y += 6;
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(10);
    doc.setTextColor(40);
    const wrapped = doc.splitTextToSize(
      asciiSafe(s.body || ''),
      PAGE_W - MARGIN * 2 - 12,
    ) as string[];
    for (const line of wrapped) {
      if (y > PAGE_H - MARGIN - TB_H - 8) break;
      doc.text(line, MARGIN + 6, y);
      y += 5;
    }
    y += 6;
  }
}

// ─────────────────────────────────────────────────────────────────────
// Public entry point
// ─────────────────────────────────────────────────────────────────────

export async function exportProjectPdf(
  meta: ProjectMeta,
  sheets: Sheet[],
  filename: string,
): Promise<void> {
  // The drawing sheet is always white paper, so the captured canvas already
  // has a print-friendly white background — no theme switching needed.
  try {
    const logo = await resolveLogo();
    const doc = new jsPDF({
      orientation: 'landscape',
      unit: 'mm',
      format: 'a3',
    });

    const totalPages = sheets.length + 1;
    // Cover (page 1)
    drawCoverPage(doc, meta, sheets);
    drawTitleBlock(
      doc,
      meta,
      {
        sheetNo: 1,
        totalSheets: totalPages,
        sheetTitle: 'Cover sheet',
        drawingNo: '0001',
      },
      logo,
    );

    // One page per sheet (pages 2+)
    for (let i = 0; i < sheets.length; i += 1) {
      const sh = sheets[i];
      doc.addPage('a3', 'landscape');
      if (sh.kind === 'block' || sh.kind === 'schematic' || sh.kind === 'mechanical') {
        // eslint-disable-next-line no-await-in-loop -- order matters here
        await drawGraphSheet(doc, sh);
      } else if (sh.kind === 'requirements') {
        drawRequirementsSheet(doc, sh);
      } else if (sh.kind === 'interconnect') {
        drawInterconnectSheet(doc, sh);
      } else if (sh.kind === 'document') {
        drawDocumentSheet(doc, sh);
      }
      drawTitleBlock(
        doc,
        meta,
        {
          sheetNo: i + 2,
          totalSheets: totalPages,
          sheetTitle: sh.name,
          drawingNo: String(i + 2).padStart(4, '0'),
        },
        logo,
      );
    }

    doc.save(filename);
  } finally {
    // nothing to restore — theme is fixed
  }
}
